import { useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { ACOMODACOES } from "@/data/acomodacoes";
import { brl, calcular, validarCPF } from "@/lib/pricing";
import { Gallery } from "@/components/Gallery";
import { MaskLines, Reveal, Eyebrow } from "@/components/Motion";
import { scrollToId } from "@/components/SmoothScroll";
import { CalendarioReserva } from "@/components/reserva/CalendarioReserva";
import { FormReserva } from "@/components/reserva/FormReserva";
import { ResumoPreco } from "@/components/reserva/ResumoPreco";
import { SucessoDialog } from "@/components/reserva/SucessoDialog";

const INITIAL = { nome: "", nascimento: "", cpf: "", email: "", telefone: "", acompanhante: "", hospedes: "2", pet: false, pagamento: "pix", parcelas: "1", decoracao: false, decoracao_opcao: "", observacoes: "" };

const validar = (f) => {
  const e = {};
  if (f.nome.trim().split(/\s+/).length < 2) e.nome = "Informe o nome completo";
  if (!f.nascimento) e.nascimento = "Informe a data de nascimento";
  if (!validarCPF(f.cpf)) e.cpf = "CPF inválido";
  if (!/^\S+@\S+\.\S+$/.test(f.email)) e.email = "E-mail inválido";
  if (f.telefone.replace(/\D/g, "").length < 10) e.telefone = "Telefone inválido";
  if (f.acompanhante.trim().length < 3) e.acompanhante = "Nome do acompanhante é obrigatório";
  if (f.decoracao && !f.decoracao_opcao) e.decoracao_opcao = "Escolha uma opção de decoração";
  return e;
};

export default function AcomodacaoPage() {
  const { slug } = useParams();
  const idx = ACOMODACOES.findIndex((x) => x.slug === slug);
  const a = ACOMODACOES[idx];
  const [range, setRange] = useState();
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [sucesso, setSucesso] = useState(null);
  const calc = useMemo(() => calcular(a?.preco ?? 0, range, form.pet, form.decoracao ? form.decoracao_opcao : null), [a, range, form.pet, form.decoracao, form.decoracao_opcao]);

  if (!a) return <Navigate to="/" replace />;
  const prev = ACOMODACOES[(idx - 1 + ACOMODACOES.length) % ACOMODACOES.length];
  const next = ACOMODACOES[(idx + 1) % ACOMODACOES.length];
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const enviar = async () => {
    if (!calc.noites) {
      toast.error("Escolha as datas de check-in e check-out no calendário.");
      scrollToId("reserva");
      return;
    }
    const e = validar(form);
    setErrors(e);
    if (Object.keys(e).length) {
      toast.error("Verifique os campos destacados.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        acomodacao: a.slug,
        checkin: format(range.from, "yyyy-MM-dd"),
        checkout: format(range.to, "yyyy-MM-dd"),
        hospedes: Number(form.hospedes),
        parcelas: form.pagamento === "cartao" ? Number(form.parcelas) : 1,
        decoracao_opcao: form.decoracao ? form.decoracao_opcao : null,
      };
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/reservas`, payload);
      setSucesso({ nome: a.nome, total: data?.total ?? calc.total, periodo: `${format(range.from, "dd/MM/yyyy")} a ${format(range.to, "dd/MM/yyyy")}` });
      setForm(INITIAL);
      setRange(undefined);
      setErrors({});
    } catch (err) {
      toast.error("Não foi possível enviar sua reserva. Tente novamente ou fale pelo WhatsApp (12) 99623-6240.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="accommodation-page" className="bg-[#f4f0e6] pt-28">
      <section className="mx-auto max-w-7xl px-5 sm:px-8">
        <Link to="/#acomodacoes" data-testid="back-to-list-link" className="inline-flex items-center gap-2 text-sm text-[#3d4a34] hover:text-[#a85d38]">
          <ArrowLeft className="h-4 w-4" /> Todas as acomodações
        </Link>
        <div className="mt-8 grid items-end gap-6 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <p className="mb-4 text-xs uppercase tracking-[0.26em] text-[#5b7a1a]">
              {a.categoria}
              {a.area ? ` · ${a.area}` : ""}
            </p>
            <MaskLines key={a.slug} testid="accommodation-title" lines={[a.nome]} className="text-4xl font-bold leading-[0.95] tracking-[-0.045em] text-[#1a382b] sm:text-6xl lg:text-7xl" />
          </div>
          <div className="flex items-end justify-between gap-6 lg:col-span-4 lg:flex-col lg:items-end">
            <p data-testid="accommodation-price" className="text-[#1a382b]">
              <span className="text-3xl font-bold">{brl(a.preco)}</span>
              <span className="text-sm text-[#5f6e62]"> / diária</span>
            </p>
            <button
              data-testid="scroll-to-booking-button"
              onClick={() => scrollToId("reserva")}
              className="rounded-full bg-[#1a382b] px-7 py-3.5 text-xs font-bold uppercase tracking-[0.16em] text-[#f3efe4] transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-[#244b39]"
            >
              Escolher datas
            </button>
          </div>
        </div>
        <Reveal className="mt-10">
          <Gallery key={a.slug} fotos={a.fotos} alt={a.nome} aspect="aspect-[4/3] md:aspect-[16/8]" thumbs testid="accommodation-gallery" />
          {a.ilustrativas && <p className="mt-2 text-xs text-[#5f6e62]">Fotos ilustrativas — imagens reais desta acomodação em breve.</p>}
        </Reveal>
        <div className="grid gap-12 py-20 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <Eyebrow>Sobre a acomodação</Eyebrow>
            <p data-testid="accommodation-description" className="text-lg leading-relaxed text-[#263228] md:text-xl">
              {a.descricao}
            </p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <Eyebrow>Destaques</Eyebrow>
            <ul data-testid="accommodation-tags" className="flex flex-wrap gap-2">
              {a.tags.map((t) => (
                <li key={t} className="rounded-full border border-[#c9c0aa] bg-white px-4 py-2 text-sm text-[#1a382b]">
                  {t}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section id="reserva" data-testid="booking-section" className="bg-[#ebe5d6] py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <Eyebrow>Reserva</Eyebrow>
            <h2 className="text-3xl font-bold tracking-[-0.035em] text-[#1a382b] sm:text-5xl">1. Escolha as datas</h2>
          </Reveal>
          <div className="mt-10">
            <CalendarioReserva range={range} setRange={setRange} />
          </div>
          <h2 className="mt-20 text-3xl font-bold tracking-[-0.035em] text-[#1a382b] sm:text-5xl">2. Seus dados</h2>
          <div className="mt-10 grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <FormReserva form={form} set={set} errors={errors} total={calc.total} maxHospedes={a.slug === "casa-de-campo-05" ? 10 : 4} />
            </div>
            <div className="lg:col-span-5">
              <div className="lg:sticky lg:top-28">
                <ResumoPreco a={a} calc={calc} form={form} loading={loading} onSubmit={enviar} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <nav className="mx-auto grid max-w-7xl grid-cols-2 gap-4 px-5 py-14 sm:px-8">
        <Link to={`/acomodacao/${prev.slug}`} data-testid="prev-accommodation-link" className="group">
          <span className="text-xs uppercase tracking-[0.2em] text-[#5f6e62]">Anterior</span>
          <span className="mt-2 flex items-center gap-2 text-lg font-bold text-[#1a382b] sm:text-2xl">
            <ArrowLeft className="h-5 w-5 transition-transform group-hover:-translate-x-1" /> {prev.nome}
          </span>
        </Link>
        <Link to={`/acomodacao/${next.slug}`} data-testid="next-accommodation-link" className="group text-right">
          <span className="text-xs uppercase tracking-[0.2em] text-[#5f6e62]">Próxima</span>
          <span className="mt-2 flex items-center justify-end gap-2 text-lg font-bold text-[#1a382b] sm:text-2xl">
            {next.nome} <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </span>
        </Link>
      </nav>
      <SucessoDialog data={sucesso} onClose={() => setSucesso(null)} />
    </main>
  );
}
